<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    title="While you were away"
    :subtitle="subtitle"
    :icon="ICONS.actions.timer"
    closeLabel="Close summary"
    wide
    @close="emit('close')"
  >
    <div class="stack">
      <UiPanel v-for="entry in summaries" :key="entry.id" class="offline-summary__panel">
        <UiSectionHeader>
          <template #title>{{ entry.title }}</template>
          <template #actions>
            <UiChip v-if="entry.status" :variant="entry.status.variant ?? 'muted'">
              {{ entry.status.label }}
            </UiChip>
          </template>
        </UiSectionHeader>
        <div v-if="entry.subtitle" class="offline-summary__subtitle text-muted">{{ entry.subtitle }}</div>
        <ul v-if="entry.lines.length" class="offline-summary__list">
          <li
            v-for="(line, idx) in entry.lines"
            :key="`${entry.id}-${idx}`"
            class="offline-summary__line"
            :data-tone="line.tone ?? 'default'"
          >
            {{ line.text }}
          </li>
        </ul>
        <div v-else class="text-muted">No offline activity reported.</div>
      </UiPanel>

      <div class="row" style="justify-content: flex-end;">
        <UiButton size="sm" variant="secondary" @click="emit('close')">
          <template #icon>{{ ICONS.states.active }}</template>
          Continue
        </UiButton>
      </div>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ICONS } from "../../../lib/icons";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

export type OfflineSummaryLineTone = "default" | "info" | "warning" | "success";

export type OfflineSummaryLine = {
  text: string;
  tone?: OfflineSummaryLineTone;
};

export type OfflineSummaryEntry = {
  id: string;
  title: string;
  subtitle?: string | null;
  status?: {
    label: string;
    variant?: "default" | "accent" | "muted" | "warning" | "danger" | "idle" | "active" | "paused" | "blocked";
  };
  lines: OfflineSummaryLine[];
};

const props = withDefaults(
  defineProps<{
    overlayZIndex?: number;
    summaries: OfflineSummaryEntry[];
    characterName?: string | null;
  }>(),
  {
    overlayZIndex: 4000,
    characterName: null
  }
);

const emit = defineEmits<{ close: [] }>();

const subtitle = computed(() => {
  if (props.characterName) {
    return `Summary for ${props.characterName}.`;
  }
  return "Summary of your offline progress.";
});
</script>

<style scoped>
.offline-summary__panel {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.offline-summary__subtitle {
  font-size: 0.9rem;
}

.offline-summary__list {
  margin: 0.2rem 0 0;
  padding-left: 1.2rem;
  display: grid;
  gap: 0.35rem;
  max-height: min(40vh, 18rem);
  overflow-y: auto;
}

.offline-summary__line {
  line-height: 1.35;
}

.offline-summary__line[data-tone="info"] {
  color: var(--accent-work);
}

.offline-summary__line[data-tone="success"] {
  color: var(--state-active);
}

.offline-summary__line[data-tone="warning"] {
  color: var(--state-warning);
}
</style>
